import React from 'react';
import {
  Paper,
  Typography,
  Grid,
  Box,
  Divider,
  TextField,
  Stack
} from '@mui/material';
import { MULTILINE_STYLE } from '../../constants/invoiceConstants';
import { formatCurrency } from '../../utils/invoiceCalculations';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';

/**
 * Component for displaying invoice summary and totals
 * 
 * @param {Object} props - Component props
 * @param {Object} props.totals - Invoice totals object
 * @param {string} props.notes - Invoice notes
 * @param {string} props.terms - Invoice terms and conditions
 * @param {Function} props.onNotesChange - Function to call when notes change
 * @param {Function} props.onTermsChange - Function to call when terms change
 */
const InvoiceSummary = ({
  totals,
  notes,
  terms,
  onNotesChange,
  onTermsChange
}) => {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 0.5,
        mb: 0.5,
        borderRadius: '4px',
        boxShadow: 'none',
        border: 'none',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.02)',
        },
      }}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 1 }}>
            Notes
          </Typography>
          <TextField
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            placeholder="Add notes for the customer (optional)"
            multiline
            rows={3}
            fullWidth
            size="small"
            sx={{
              ...MULTILINE_STYLE,
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px',
                fontSize: '0.875rem',
                '& textarea': {
                  padding: '8px 12px',
                  lineHeight: 1.4
                }
              }
            }}
          />

          <Typography variant="subtitle2" sx={{ fontSize: '0.875rem', fontWeight: 600, mb: 1, mt: 2 }}>
            Terms & Conditions
          </Typography>
          <TextField
            value={terms}
            onChange={(e) => onTermsChange(e.target.value)}
            placeholder="Add terms and conditions"
            multiline
            rows={3}
            fullWidth
            size="small"
            sx={{
              ...MULTILINE_STYLE,
              '& .MuiOutlinedInput-root': {
                borderRadius: '4px',
                fontSize: '0.875rem',
                '& textarea': {
                  padding: '8px 12px',
                  lineHeight: 1.4
                }
              }
            }}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <Box
            sx={{
              border: '1px solid rgba(0, 0, 0, 0.08)',
              borderRadius: '8px',
              p: 2.5,
              backgroundColor: '#ffffff',
              ml: { md: 2 },
              mb: 2,
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                fontSize: '1rem',
                fontWeight: 600,
                mb: 2,
                color: 'text.primary',
                display: 'flex',
                alignItems: 'center',
                gap: 1
              }}
            >
              <AttachMoneyIcon fontSize="small" color="primary" />
              Invoice Summary
            </Typography>

            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Subtotal
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrency(totals.subtotal)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  Discount
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, color: 'error.main' }}>
                  -{formatCurrency(totals.totalDiscount)}
                </Typography>
              </Box>

              <Divider sx={{ my: 0.5 }} />

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500 }}>
                  Taxable Amount
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {formatCurrency(totals.netAmount)}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  VAT (15%)
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatCurrency(totals.totalVat)}
                </Typography>
              </Box>

              <Divider sx={{ my: 1 }} />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  backgroundColor: '#f8faff',
                  p: 1.5,
                  borderRadius: '6px',
                  border: '1px solid rgba(0, 0, 0, 0.04)'
                }}
              >
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  Total Amount
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {formatCurrency(totals.grandTotal)}
                </Typography>
              </Box>
            </Stack>
          </Box>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default InvoiceSummary;
